// Admin management service — Auth0-correct admin lifecycle.
// Endpoints under /api/admin/management and /api/admin/account; VITE_API_URL
// already includes /api, so paths here are relative. Cookie-authenticated.

import { apiClient } from "../client";

export type AdminTier = "admin" | "super-admin";

export interface AdminInvitation {
  _id?: string;
  email: string;
  adminTier: AdminTier;
  permissions?: string[];
  status?: string;
  createdAt?: string;
  expiresAt?: string;
}

export interface InviteResult {
  success?: boolean;
  invitation: AdminInvitation;
  /** Present only in non-prod — the link the invitee uses to accept. */
  inviteLink?: string;
}

export interface AcceptResult {
  success?: boolean;
  message?: string;
}

export interface RemoveAdminResult {
  success: boolean;
  message?: string;
}

export interface ChangePasswordResult {
  success: boolean;
  message?: string;
  /** Present only in non-prod — the Auth0 password-change ticket URL. */
  ticketUrl?: string;
}

/**
 * Super-admin: invite a new admin (step 1 of 2). The invitee completes
 * registration via acceptAdminInvite with the token from the invite link.
 * POST /admin/management/invite
 */
export const inviteAdmin = async (payload: {
  email: string;
  adminTier: AdminTier;
  permissions?: string[];
}): Promise<InviteResult> => {
  const res = await apiClient.post<InviteResult>("/admin/management/invite", payload);
  return res.data;
};

/**
 * Public, token-gated: accept an admin invitation (step 2 of 2). Creates the
 * admin, who then signs in via Auth0.
 * POST /admin/management/accept
 */
export const acceptAdminInvite = async (payload: {
  token: string;
  firstName: string;
  lastName: string;
  password: string;
}): Promise<AcceptResult> => {
  const res = await apiClient.post<AcceptResult>("/admin/management/accept", payload);
  return res.data;
};

/**
 * Super-admin: remove an admin (demote-not-destroy — strips role to end-user,
 * clears adminTier/permissions, suspends the account; audit history preserved).
 * DELETE /admin/management/admins/:id (empty body).
 * Guards: 403 NO_SELF_REMOVAL, 403 CANNOT_REMOVE_SUPER_ADMIN, 404 if not an admin.
 */
export const removeAdmin = async (id: string): Promise<RemoveAdminResult> => {
  const res = await apiClient.delete<RemoveAdminResult>(`/admin/management/admins/${id}`);
  return res.data;
};

/**
 * Admin/super-admin: change the signed-in admin's password.
 * POST /admin/account/change-password with { currentPassword, newPassword }.
 */
export const changeMyPassword = async (payload: {
  currentPassword: string;
  newPassword: string;
}): Promise<ChangePasswordResult> => {
  const res = await apiClient.post<ChangePasswordResult>("/admin/account/change-password", payload);
  return res.data;
};
